// Tab »Fortbildung« im Dialog »Benutzer bearbeiten«: beide CME-Zeiträume mit Punkten,
// eingereichten Bescheinigungen und Schalter zur manuellen Befreiung (user.cme_befreit = Liste der Zeitraum-Enddaten).
import { useEffect, useState } from 'react'
import { useGlobal } from 'reactn'
import { Box, Chip, FormControlLabel, Link, Stack, Switch, Typography } from '@mui/material'
import { format } from 'date-fns'
import { DataTable } from './datatable'
import './usercme.css'

const NEEDED = 20

const fmt = (d) => { try { return format(new Date(d), 'd.M.y') } catch { return String(d) } }
// Schlüssel für cme_befreit: Enddatum des Zeitraums als YYYY-MM-DD (Backend nutzt dieselbe Form)
const endeKey = (d) => { try { return format(new Date(d), 'yyyy-MM-dd') } catch { return String(d) } }

const statuslabel = { pending: 'In Bearbeitung', granted: 'akzeptiert', rejected: 'abgelehnt' }

const beschColumns = [
    { id: 'datum', label: 'Datum', get: b => b.datum, render: b => fmt(b.datum) },
    { id: 'punkte', label: 'Punkte', get: b => Number(b.punkte), align: 'right' },
    { id: 'status', label: 'Status', get: b => b.status, render: b => statuslabel[b.status] || b.status },
    { id: 'datei', label: 'Datei', sortable: false, get: b => b.filename,
      render: b => <Link href={'https://notarzt-heinsberg.de/api/bescheinigung/' + b._id} target='_blank' rel='noreferrer'>{b.filename || 'öffnen'}</Link> },
]

function periodChip(p, manuell) {
    if (manuell || p.befreit === 'manuell') return <Chip size='small' color='primary' label='befreit (manuell)' />
    if (p.befreit === 'auto' || p.punkte === '?') return <Chip size='small' variant='outlined' label='befreit (automatisch)' />
    if (Number(p.punkte) >= NEEDED) return <Chip size='small' color='success' label='erfüllt' />
    return <Chip size='small' color='error' label={`offen – fehlen ${NEEDED - Number(p.punkte || 0)}`} />
}

export function UserCmeTab({ user, moduser, setModuser }) {
    const [axios] = useGlobal('axios')
    const [status, setStatus] = useState(null)

    useEffect(() => {
        if (user?._id) axios.get('checkbeschstatus', { params: { withreports: true, userid: user._id } }).then(r => setStatus(r.data))
        else setStatus(null)
    }, [user])

    const befreit = moduser?.cme_befreit || []
    const toggleBefreit = (key, on) => {
        const next = on ? [...new Set([...befreit, key])] : befreit.filter(k => k !== key)
        setModuser({ ...moduser, cme_befreit: next })
    }

    if (!status) return <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>Lade Fortbildungsdaten…</Typography>
    if (status.error) return <Typography variant='body2' color='error' sx={{ p: 2 }}>Fehler: {status.error}</Typography>

    const periods = [
        { key: 'pre', label: 'Vorzeitraum (abgeschlossen)', data: status.pre },
        { key: 'akt', label: 'Aktueller Zeitraum', data: status.akt },
    ].filter(p => p.data)

    return (
        <Stack spacing={2}>
            <Typography variant='body2' color='text.secondary'>
                Erste notärztliche Tätigkeit: <b>{fmt(status.nastart)}</b> · Zweijahres-Zyklen, {NEEDED} Punkte je Zeitraum
            </Typography>
            {periods.map(p => {
                const key = endeKey(p.data.ende)
                const manuell = befreit.includes(key)
                return (
                    <Box key={p.key} className={'cmeperiod' + (manuell ? ' befreit' : '')}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent='space-between'>
                            <div>
                                <Typography variant='subtitle1'>{p.label}</Typography>
                                <Typography variant='body2' color='text.secondary'>{fmt(p.data.start)} – {fmt(p.data.ende)} · anerkannt: <b>{p.data.punkte}</b> / {NEEDED}</Typography>
                            </div>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                {periodChip(p.data, manuell)}
                                <FormControlLabel label='befreit' control={<Switch size='small' checked={manuell} onChange={e => toggleBefreit(key, e.target.checked)} />} />
                            </Stack>
                        </Stack>
                        {(p.data.besch?.length > 0) ?
                            <DataTable columns={beschColumns} rows={p.data.besch} rowKey={b => b._id} defaultSort='datum' defaultDir='desc' />
                            : <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>Keine Bescheinigungen in diesem Zeitraum.</Typography>
                        }
                    </Box>
                )
            })}
            <Typography variant='caption' color='text.secondary'>
                Die Befreiung wird mit »speichern« übernommen und gilt nur für den jeweiligen Zeitraum.
            </Typography>
        </Stack>
    )
}
